import { ProductBundleRowType } from "../../components/Editable/ProductBundle/RowType";
import {hasErrorFn, TableName} from "./options";

function generateKey() {
    return Math.random().toString(36);
}

let counter = 1;

export function findChild(items, key) {
    for (let idx = 0; idx < items.length; idx += 1) {
        const item = items[idx];
        if (item.key === key)
            return item;

        if (Array.isArray(item.children)) {
            const child = findChild(item.children, key);
            if (child)
                return child;
        }
    }
    return undefined;
}

function findChildrenIterFn(items, key, actionWhenFound, parentItem = undefined) {
    for (let idx = 0; idx < items.length; idx += 1) {
        const item = items[idx];
        if (item.key === key) {
            actionWhenFound(item, idx, items, parentItem);
            break;
        }
        if (Array.isArray(item.children))
            findChildrenIterFn(item.children, key, actionWhenFound, item);
    }
    return items;
}

function generateKeyIterFn(item, parentKey = undefined) {
    const newItem = {...item};
    newItem.key = newItem.key || generateKey();
    newItem.parentKey = parentKey;
    newItem.line = counter;

    // in transactions' drag-n-drop component, reset displayNumber after every 'subtitle' or 'subtotal'
    if (newItem.type === 'subtotal' || newItem.type === 'subtitle')
        counter = 0;
    counter += 1;

    if (Array.isArray(newItem.children))
        newItem.children = newItem.children.map(child => generateKeyIterFn(child, newItem.key));
    return newItem;
}

export const EditableReduxHelper = {
    generateKey(items) {
        counter = 1;
        return items.map(item => generateKeyIterFn(item));
    },

    replaceNullWithUndefined(items) {
        return items.map(function iter(item) {
            const newItem = {...item};

            Object.keys(newItem).forEach(key => {
                const value = newItem[key];
                if (value === null)
                    newItem[key] = undefined;
            });

            if (Array.isArray(newItem.children))
                newItem.children.forEach(iter);
            return newItem;
        });
    },

    setItem(name, state, action, context = {fields: {}}) {
        const items = [...state.items];
        const {key} = action.item;
        const newItem = action.item;

        const updateItems = findChildrenIterFn(items, key, (item, idx, items) => {
            const updatedItem = {...item, ...newItem};
            updatedItem.dirty = hasErrorFn(name, updatedItem, action.params, context);

            // eslint-disable-next-line no-param-reassign
            items[idx] = updatedItem;
        },
        );

        return {
            ...state,
            items: updateItems,
            refresh: state.refresh + 1,
        };
    },

    addItem(name, state, action, context = {fields: {}}) {
        const items = [...state.items];
        const {parentKey} = action;

        const newItem = { ...action.item };
        newItem.dirty = hasErrorFn(name, newItem, action.params, context);
        newItem.key = generateKey();

        let updateItems;
        if (parentKey) {
            updateItems = findChildrenIterFn(items, parentKey, item => {
                // eslint-disable-next-line no-param-reassign
                item.children = [...item.children, newItem];
            });
        }
        else if (action.index !== undefined) {
            // Insert at specific index (for subtotal/subtitle placement)
            // Using splice to insert
            updateItems = [...items];
            updateItems.splice(action.index, 0, newItem);
        }
        else {
            updateItems = [...items, newItem];
        }

        // if is product bundle table, fix bundle_discount row to be the last item
        if (name === TableName.product_bundle &&
            updateItems[updateItems.length - 1].type !== ProductBundleRowType.BUNDLE_DISCOUNT
        ) {
            const index = updateItems.findIndex(item => item.type === ProductBundleRowType.BUNDLE_DISCOUNT);
            if (index !== -1) {
                const obj = updateItems.splice(index, 1)[0];
                updateItems.push(obj);
            }
        }

        return {
            ...state,
            items: EditableReduxHelper.generateKey(updateItems),
            editLine: action.editItem ? newItem.key : state.editLine,
            refresh: state.refresh + 1,
        };
    },

    removeItem(name, state, action) {
        const items = [...state.items];
        const {key} = action;

        const updateItems = findChildrenIterFn(items, key, (item, idx, items, parentItem) => {
            if (parentItem && parentItem.children)
                parentItem.children.splice(idx, 1);
            else
                items.splice(idx, 1);
        },
        );

        return {
            ...state,
            // state.activeTable will be null if no form/bank item rows are currently being focused (due to EditableActions.ClEAR_ALL_EDIT_LINE case in reducer)
            // Since we have '...state' here, after this removeItem function is called, activeTable possibly can still be null.
            // This will cause summary table to not be updated, as calculation of summary table (eg calculateAll.js) requires an 'activeTable' value.
            // So here we set shouldPreserveActiveTable = true, so that in reducer we can set back the activeTable,
            // and calculations can be done correctly.
            shouldPreserveActiveTable: true,
            items: EditableReduxHelper.generateKey(updateItems),
            editLine: action.key === state.editLine ? false : state.editLine,
            refresh: state.refresh + 1,
        };
    },

    addItems(name, state, action, context = {fields: {}}) {
        const items = [...state.items];
        const newItems = action.items.map(item => ({
            ...item,
            dirty: hasErrorFn(name, item, action.params, context),
        }));
        const updateItems = [...items, ...newItems];
        return {
            ...state,
            items: EditableReduxHelper.generateKey(updateItems),
        };
    },

    getExpandedRowKeys(items) {
        const expandedRowKeys = [];
        items.forEach(function iterFn(item) {
            if (item.children)
                expandedRowKeys.push(item.key);
            if (Array.isArray(item.children))
                item.children.forEach(iterFn);
        });

        return expandedRowKeys;
    },
};
